import { Components } from "react-markdown";
import M360MarkdownLink from "@/lib/M360MarkdownLink";
import { normalizeM360ImageSrc } from "@/lib/m360Paths";

/**
 * Conservative markdown detection for mixed plain-text / markdown fields.
 * Matches headings, bold, quotes, code, and links — not single * or _
 * so staff notes like "2 * 3" or "john_doe" stay plain.
 * A lone `1. Called member` / `- Left voicemail` line stays plain; two or more
 * list markers still count as markdown.
 */
const NON_LIST_MARKDOWN_PATTERN =
  /(^|\n)\s{0,3}#{1,6}\s+|\*\*[^*\n]+\*\*|__[^_\n]+__|~~[^~\n]+~~|(^|\n)\s{0,3}>\s+|`{1,3}[^\n`]+`{1,3}|\[[^\]]+\]\([^)]+\)/;
const LIST_LINE_PATTERN = /^\s{0,3}(?:[-*+]|\d+\.)\s+/;

function countMarkdownListLines(text: string): number {
  return text.split("\n").filter((line) => LIST_LINE_PATTERN.test(line)).length;
}

export function isLikelyMarkdown(text: string | null | undefined): boolean {
  if (!text) return false;
  if (NON_LIST_MARKDOWN_PATTERN.test(text)) return true;
  return countMarkdownListLines(text) >= 2;
}

/** Keep pasted single newlines as markdown paragraphs (CommonMark otherwise collapses them). */
export function preserveMarkdownLineBreaks(text: string): string {
  return text
    .split("\n")
    .map((line) => line.trimEnd())
    .join("\n\n")
    .replace(/\n{3,}/g, "\n\n");
}

/** Strip common markdown markers for clipped / plain-text previews. */
export function stripMarkdownSyntax(text: string): string {
  return text
    .replace(/```[\s\S]*?```/g, (block) => block.replace(/```/g, "").trim())
    .replace(/`([^`]+)`/g, "$1")
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/__([^_]+)__/g, "$1")
    .replace(/~~([^~]+)~~/g, "$1")
    .replace(/(^|\n)\s{0,3}#{1,6}\s+/g, "$1")
    .replace(/(^|\n)\s{0,3}[-*+]\s+/g, "$1")
    .replace(/(^|\n)\s{0,3}\d+\.\s+/g, "$1")
    .replace(/(^|\n)\s{0,3}>\s+/g, "$1")
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    .trim();
}

export const formatMarkdownText = (text: string) => {
  return (
    text
      // Convert HTML image tags to markdown
      .replace(/<img[^>]*src="([^"]*)"[^>]*alt="([^"]*)"[^>]*>/g, "![$2]($1)")
      // Convert HTML lists to markdown
      .replace(/<\/?ul>/g, "\n")
      .replace(/<\/?ol>/g, "\n")
      .replace(/<li>(.*?)<\/li>/g, "* $1\n")
      // Convert HTML text formatting to markdown
      .replace(/<(strong|b)>(.*?)<\/(strong|b)>/g, "**$2**")
      .replace(/<(em|i)>(.*?)<\/(em|i)>/g, "_$2_")
      .replace(/<del>(.*?)<\/del>/g, "~~$2~~")
      .replace(/<code>(.*?)<\/code>/g, "`$1`")
      .replace(/<pre>(.*?)<\/pre>/g, "```\n$1\n```")
      .replace(/<h([1-6])>(.*?)<\/h\1>/g, (_, level, content) => "\n" + "#".repeat(level) + " " + content + "\n")
      // Convert HTML paragraphs to markdown
      .replace(/<p>(.*?)<\/p>/g, "$1\n\n")
      // Convert HTML line breaks to newlines
      .replace(/<br\s*\/?>/g, "\n")
      // Convert HTML entities
      .replace(/&nbsp;/g, " ")
      .replace(/&rsquo;/g, "'")
      .replace(/&ndash;/g, "-")
      .replace(/&mdash;/g, "—")
      .replace(/&quot;/g, '"')
      .replace(/&amp;/g, "&")
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      // add new lines before headings
      .replace(/(\s#)/g, "\n$1")
      // add new lines before list items
      .replace(/\s-/g, "\n-")
      // gracefully handle carriage return
      .replace(/\r/g, "\n")
      // Clean up multiple newlines
      .replace(/\n{3,}/g, "\n\n")
      .trim()
  );
};

const baseMarkdownComponents: Components = {
  h1: ({ node, ...props }) => <h1 className="text-headline-small" {...props} />,
  h2: ({ node, ...props }) => <h2 className="text-title-large" {...props} />,
  h3: ({ node, ...props }) => <h3 className="text-title-medium" {...props} />,
  h4: ({ node, ...props }) => <h4 className="text-title-small" {...props} />,
  h5: ({ node, ...props }) => <h5 className="text-label-large" {...props} />,
  h6: ({ node, ...props }) => <h6 className="text-label-medium" {...props} />,
  p: ({ node, ...props }) => <p className="text-body-medium mb-1" {...props} />,
  ul: ({ node, ...props }) => <ul className="mb-2 ml-4 list-disc space-y-1">{props.children}</ul>,
  ol: ({ node, ...props }) => <ol className="mb-2 ml-4 list-decimal space-y-1">{props.children}</ol>,
  li: ({ node, ...props }) => <li className="text-body-medium" {...props} />,
  strong: ({ node, ...props }) => <strong className="font-semibold" {...props} />,
  em: ({ node, ...props }) => <em className="italic" {...props} />,
  hr: () => <hr className="my-3 border-sys-brand-outline-variant" />,
  blockquote: ({ node, ...props }) => (
    <blockquote
      className="my-2 border-l-4 border-sys-brand-primary/40 pl-3 text-body-medium text-sys-brand-on-surface-variant"
      {...props}
    />
  ),
  code: ({ node, className, children, ...props }) => {
    const isBlock = className?.includes("language-");
    if (isBlock) {
      return (
        <code className={`block whitespace-pre-wrap text-body-small ${className ?? ""}`} {...props}>
          {children}
        </code>
      );
    }

    return (
      <code className="rounded bg-sys-brand-surface-container-high px-1 py-0.5 font-mono text-body-small" {...props}>
        {children}
      </code>
    );
  },
  pre: ({ node, ...props }) => (
    <pre
      className="my-2 overflow-x-auto rounded-lg border border-sys-brand-outline-variant bg-sys-brand-surface-container-low p-3"
      {...props}
    />
  ),
  table: ({ node, ...props }) => (
    <div className="my-2 overflow-x-auto rounded-lg border border-sys-brand-outline-variant">
      <table className="min-w-full border-collapse text-body-small" {...props} />
    </div>
  ),
  thead: ({ node, ...props }) => <thead className="bg-sys-brand-surface-container-low" {...props} />,
  tbody: ({ node, ...props }) => <tbody className="divide-y divide-sys-brand-outline-variant" {...props} />,
  tr: ({ node, ...props }) => <tr {...props} />,
  th: ({ node, ...props }) => <th className="px-3 py-2 text-left font-semibold text-sys-brand-on-surface" {...props} />,
  td: ({ node, ...props }) => <td className="px-3 py-2 text-sys-brand-on-surface" {...props} />
};

/** Default markdown map for long-form content (AiCard, jobs, events). */
export const markdownComponents: Components = baseMarkdownComponents;

/** Interaction notes: in-app links, no remote images. */
export const noteMarkdownComponents: Components = {
  ...baseMarkdownComponents,
  a: ({ node, href, children, onClick, target: _target, rel: _rel, ...props }) => (
    <M360MarkdownLink
      href={href}
      onClick={(event) => {
        event.stopPropagation();
        onClick?.(event);
      }}
      {...props}
    >
      {children}
    </M360MarkdownLink>
  ),
  img: ({ alt }) => <span className="text-body-small text-sys-brand-on-surface-variant">[{alt ?? "image"}]</span>
};

/** Chat playground markdown — adds in-app Member 360 deep links and compact media. */
export const chatMarkdownComponents: Components = {
  ...baseMarkdownComponents,
  a: ({ node, href, children, target: _target, rel: _rel, ...props }) => (
    <M360MarkdownLink href={href} {...props}>
      {children}
    </M360MarkdownLink>
  ),
  img: ({ node, alt, src, ...props }) => {
    const safeSrc = normalizeM360ImageSrc(src);
    if (!safeSrc) {
      return <span className="text-body-small text-sys-brand-on-surface-variant">[{alt ?? "image"}]</span>;
    }

    return (
      <img
        src={safeSrc}
        alt={alt ?? "Profile photo"}
        className="my-1 h-10 w-10 rounded-full object-cover"
        loading="lazy"
        {...props}
      />
    );
  }
};
